import { useEffect, useState } from 'react';

import {
  Anchor,
  Box,
  Button,
  Divider,
  FormHelp,
  Heading,
  Icon,
  IconButton,
  Modal,
  Paragraph,
} from '@zillow/constellation-10';
import { IconChevronLeftFilled, IconChevronRightFilled, IconDeleteFilled } from '@zillow/constellation-icons';

import { TextareaHelper } from './TextareaHelper';

const RIGHT = '-1';
const LEFT = '+1';

export type PhotoProps = {
  caption: string;
  id: string;
  rank: number | string;
  url: string;
  error?: string;
  feedId?: string;
  feedListingId?: string;
  isAppropriate?: boolean;
  thumbUrl?: string;
  errorDto?: {
    errorCode?: string;
    errorMessage?: string;
    replacementPhotoId?: string;
  };
};

const getUrlSrc = (
  photo: PhotoProps,
  getPhotoUrls: (
    isRunningInProd: boolean,
    photoKey: string,
  ) => {
    medium: string;
    thumb: string;
  },
  isRunningInProd: boolean,
) => {
  if (photo?.errorDto?.errorCode) {
    if (photo?.errorDto?.replacementPhotoId) {
      return getPhotoUrls(isRunningInProd, photo?.errorDto?.replacementPhotoId).medium;
    }

    return '';
  }

  return photo.url ? photo.url.replace('http://photo', 'https://photonet') : '';
};

type Props = {
  onClose: () => void;
  idx: number;
  additionalDisabledChecks?: boolean;
  /**
   * disable the caption feature
   *
   * captions are not supported on zillow
   * support date is unknown
   */
  isCaptionDisabled?: boolean;
  photos: Array<PhotoProps>;
  onDeletePhoto: (args: { photoIdAtSlidePosition: string; onComplete: () => void; slidePosition: number }) => void;
  onCaptionUpdate?: (args: { photoId: string; caption: string; onComplete: () => void; slidePosition: number }) => void;
  onClearError: (args: { photoAtSlidePosition: PhotoProps; slidePosition: number }) => void;
  getPhotoUrls?: (
    isRunningInProd: boolean,
    photoKey: string,
  ) => {
    medium: string;
    thumb: string;
  };
  isRunningInProd?: boolean;
};

export const PhotoDetailsModal = ({
  additionalDisabledChecks,
  idx,
  isCaptionDisabled = false,
  onCaptionUpdate,
  onClearError,
  onClose,
  onDeletePhoto,
  photos,
  getPhotoUrls = () => ({ medium: '', thumb: '' }),
  isRunningInProd = false,
}: Props) => {
  const [isCaptionSaving, setIsCaptionSaving] = useState(false);
  const [isPhotoDeleting, setIsPhotoDeleting] = useState(false);
  const [slidePosition, setSlidePosition] = useState(idx);

  const isActionButtonDisabled = additionalDisabledChecks || isCaptionSaving || isPhotoDeleting;

  useEffect(() => {
    photos.forEach((image: PhotoProps) => {
      new Image().src = image.url;
    });

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        getNextSlideIdx(LEFT);
      } else if (e.key === 'ArrowRight') {
        getNextSlideIdx(RIGHT);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [slidePosition, photos.length]);

  const getNextSlideIdx = (direction: string) => {
    let nextSlideIdx;
    if (direction === LEFT) {
      nextSlideIdx = slidePosition - 1 < 0 ? photos.length - 1 : slidePosition - 1;
    } else {
      nextSlideIdx = slidePosition + 1 > photos.length - 1 ? 0 : slidePosition + 1;
    }
    setSlidePosition(nextSlideIdx);
  };

  if (idx < 0 || !photos.length || !photos[slidePosition]) {
    onClose();
    return null;
  }

  return (
    <Modal.Root size={{ base: 'fullScreen', md: 'md' }} open={true} onOpenChange={(open) => !open && onClose()}>
      <Modal.Portal>
        <Modal.Backdrop lockScroll>
          <Modal.Content>
            <Modal.Header>
              <Heading level={1}>Edit photo</Heading>
            </Modal.Header>
            <Modal.CloseButton />
            <Divider />
            <Modal.Body css={{ paddingInline: '0 !important', paddingBlock: '0 !important' }}>
              <Box css={{ width: '100%', overflow: 'hidden', userSelect: 'none' }}>
                <Box css={{ flex: '1 0 100%' }} key={`${photos[slidePosition].id}-carousel-slot`}>
                  <Box
                    key={`photo-${photos[slidePosition].id}`}
                    css={{ display: 'flex', flexDirection: { base: 'column', lg: 'row' } }}
                  >
                    <Box css={{ flexBasis: isCaptionDisabled ? '100%' : '50%' }}>
                      <Box
                        css={{
                          alignItems: 'center',
                          backgroundPosition: 'center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: 'contain',
                          backgroundColor: 'bg.accent.gray.hero',
                          display: 'flex',
                          aspectRatio: '4/3',
                          justifyContent: 'space-between',
                          padding: 'tight',
                          width: '100%',
                        }}
                        style={{
                          backgroundImage: `url(${getUrlSrc(photos[slidePosition], getPhotoUrls, isRunningInProd)})`,
                        }}
                      >
                        {photos.length > 1 && (
                          <>
                            <IconButton
                              size="sm"
                              emphasis="outlined"
                              tone="neutral"
                              shape="circle"
                              onClick={() => getNextSlideIdx(LEFT)}
                              title="Previous"
                            >
                              <IconChevronLeftFilled />
                            </IconButton>
                            <IconButton
                              size="sm"
                              emphasis="outlined"
                              tone="neutral"
                              shape="circle"
                              onClick={() => getNextSlideIdx(RIGHT)}
                              title="Next"
                            >
                              <IconChevronRightFilled />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </Box>

                    {!isCaptionDisabled && (
                      <Box
                        css={{
                          paddingLeft: { base: '0', lg: 'default' },
                          marginRight: { base: '0', lg: 'tight' },
                          paddingTop: { base: 'default', lg: '0' },
                          flexBasis: '50%',
                          flexDirection: 'column',
                        }}
                      >
                        <div data-testid="Caption">
                          <TextareaHelper
                            isDisabled={isActionButtonDisabled}
                            initialValue={photos[slidePosition].caption}
                            onClearError={() =>
                              onClearError({ photoAtSlidePosition: photos[slidePosition], slidePosition })
                            }
                            handleCaptionBlur={(caption: string) => {
                              setIsCaptionSaving(true);
                              if (onCaptionUpdate) {
                                onCaptionUpdate({
                                  photoId: photos[slidePosition].id,
                                  caption,
                                  onComplete: () => {
                                    setIsCaptionSaving(false);
                                  },
                                  slidePosition: slidePosition,
                                });
                              }
                            }}
                          />

                          {photos[slidePosition].error && (
                            <FormHelp error={true}>{photos[slidePosition].error}</FormHelp>
                          )}
                        </div>

                        {Boolean(
                          photos[slidePosition]?.isAppropriate === false || photos[slidePosition]?.errorDto?.errorCode,
                        ) && (
                          <>
                            <Paragraph css={{ marginTop: 'looser', color: 'text.feedback.critical.hero' }}>
                              {photos[slidePosition]?.errorDto?.errorCode &&
                              photos[slidePosition]?.errorDto?.errorMessage
                                ? photos[slidePosition]?.errorDto?.errorMessage
                                : 'This photo is inappropriate or irrelevant for rental listings and will not appear on Zillow Rental Network.'}
                            </Paragraph>
                            <Paragraph css={{ marginTop: 'default' }}>
                              To understand why this image has been flagged, refer to this{' '}
                              <Anchor
                                target="_blank"
                                href="https://www.zillow.help/article/photos-flagged-on-zillow-rental-manager-listings-zd30234472640147"
                              >
                                help article
                              </Anchor>
                              .
                            </Paragraph>
                          </>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </Modal.Body>
            <Divider />
            <Modal.Footer>
              <Button
                aria-label="Delete photo"
                data-testid="PhotoDetailsModal-DeletePhotoButton"
                tone="critical"
                emphasis="outlined"
                disabled={isActionButtonDisabled}
                loading={isPhotoDeleting}
                icon={<Icon render={<IconDeleteFilled />} />}
                onClick={() => {
                  setIsPhotoDeleting(true);
                  onDeletePhoto({
                    photoIdAtSlidePosition: photos[slidePosition].id,
                    onComplete: () => {
                      setIsPhotoDeleting(false);
                      if (photos.length === 1) {
                        setSlidePosition(0);
                      }
                    },
                    slidePosition,
                  });
                }}
              >
                Delete photo
              </Button>
            </Modal.Footer>
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
};
